import { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Bookmark, CornerDownRight, Minus, Plus } from "lucide-react";
import UserLink from "@/components/UserLink";
import TitleTag from "@/components/TitleTag";
import SportIcon from "@/components/SportIcon";
import FeedPostCard from "@/components/FeedPostCard";
import AdSlot from "@/components/AdSlot";
import { useAdSetting } from "@/lib/adSettings";
import { tierFor } from "@/lib/tiers";
import { feedHeaderBg } from "@/components/FeedGlow";

function timeAgo(iso) {
    try {
        const d = new Date(iso);
        const s = Math.floor((Date.now() - d.getTime()) / 1000);
        if (s < 60) return "just now";
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
        if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
        return `${d.toLocaleDateString(undefined, { month: "short" })} ${d.getDate()} ${d.getFullYear()}`;
    } catch { return ""; }
}

const COUNT_OPTIONS = [3, 5, 10, 25, 50];

// Only show the subject when it's a real title — not an echo of the body's first
// line (legacy posts auto-filled the subject from the body, causing duplicate text).
function hasDistinctSubject(p) {
    const subj = (p.subject || "").trim();
    if (!subj) return false;
    const firstLine = (p.body || "").split("\n")[0].trim().toLowerCase();
    const s = subj.toLowerCase();
    return firstLine !== s && !firstLine.startsWith(s);
}

// Group feed items by author, preserving the incoming order (already sorted by the API).
function groupByAuthor(items) {
    const groups = [];
    const byKey = {};
    for (const p of items) {
        const key = p.author_username || p.author_id || p.id;
        if (!byKey[key]) {
            byKey[key] = { key, author_username: p.author_username, author_photo: p.author_photo, author_thanks_count: p.author_thanks_count || 0, posts: [] };
            groups.push(byKey[key]);
        }
        byKey[key].posts.push(p);
    }
    return groups;
}

// Left color bar per board type.
const TYPE_COLOR = { team: "#ff4500", trainer: "#2196f3", trainer_listing: "#2196f3", event: "#22c55e", wall: "#a855f7", forum: "#c19a6b" };

// Grey sport icon + grey name (the color bar signals the type, so no type-label prefix).
function NameLine({ p, size = 16 }) {
    const isWall = p.entity?.type === "wall";
    return (
        <span className="flex items-center gap-1.5 min-w-0">
            {!isWall && p.entity?.sport && <SportIcon sport={p.entity.sport} variant="line" size={size} className="text-[#a0a0a0] shrink-0" />}
            <span className="font-semibold text-[#a0a0a0] group-hover:text-white truncate">{isWall ? "Wall post" : (p.entity?.name || "Listing")}</span>
        </span>
    );
}

export function SinglePostCard({ p }) {
    const color = TYPE_COLOR[p.entity?.type] || "#ff4500";
    return (
        <div className="border border-[#2a2a2a] bg-[#141414] p-4" style={{ borderLeft: `4px solid ${color}` }} data-testid={`feed-item-${p.id}`}>
            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                <Link to={p.entity?.link || "/"} className="group text-sm min-w-0" data-testid={`feed-item-name-${p.id}`}>
                    <NameLine p={p} size={16} />
                </Link>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] flex items-center gap-1.5 min-w-0">
                    <span className="text-[#555]">·</span>
                    <UserLink username={p.author_username} prefix={false} /><TitleTag title={p.author_title} isPro={p.author_is_pro} /> · {timeAgo(p.created_at)}
                </span>
            </div>
            <div className="mt-2">
                {hasDistinctSubject(p) ? <div className="font-display text-lg tracking-wide">{p.subject}</div> : null}
                <p className="text-sm text-[#cfcfcf] mt-2 whitespace-pre-line line-clamp-4">{p.body}</p>
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
                <Link to={`${p.entity?.link || "/"}#post-${p.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#ff4500] hover:opacity-80 shrink-0" data-testid={`feed-item-open-${p.id}`}>
                    <CornerDownRight className="h-3 w-3" /> {p.reply_count} {p.reply_count === 1 ? "reply" : "replies"} · View &amp; reply
                </Link>
                {p.reactions && p.reactions.total > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-[#a0a0a0]" data-testid={`feed-item-reactions-${p.id}`}>
                        {Object.entries(p.reactions.counts).map(([e, n]) => (
                            <span key={e} className="inline-flex items-center gap-0.5">{e} {n}</span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// Several posts by the same person collapsed into one card.
function GroupedCard({ group }) {
    const CAP = 4;
    const posts = group.posts;
    const shown = posts.slice(0, CAP);
    const uname = group.author_username || "";
    const ring = tierFor(group.author_thanks_count || 0).color;
    return (
        <div className="border border-[#2a2a2a] bg-[#141414] p-4" data-testid={`feed-group-${group.key}`}>
            <Link to={`/u/${uname}?tab=posts`} className="flex items-center gap-3 group pb-3 border-b border-[#2a2a2a]">
                {group.author_photo ? (
                    <img src={group.author_photo} alt="" style={{ borderColor: ring }} className="h-9 w-9 rounded-full object-cover border-2 bg-white" />
                ) : (
                    <span style={{ borderColor: ring, color: ring }} className="h-9 w-9 rounded-full flex items-center justify-center border-2 bg-[#1a1a1a] text-sm font-display">{(uname || "?")[0].toUpperCase()}</span>
                )}
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate group-hover:text-[#ff4500]">{uname}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Posted {posts.length} times · last {timeAgo(posts[0].created_at)}</div>
                </div>
            </Link>
            <div className="space-y-1 mt-2">
                {shown.map((p) => {
                    const color = TYPE_COLOR[p.entity?.type] || "#ff4500";
                    return (
                        <Link key={p.id} to={`${p.entity?.link || "/"}#post-${p.id}`} className="flex gap-3 py-2 pl-3 group" style={{ borderLeft: `3px solid ${color}` }} data-testid={`feed-group-item-${p.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 min-w-0" data-testid={`feed-group-item-name-${p.id}`}>
                                    <div className="text-xs group min-w-0 shrink"><NameLine p={p} size={14} /></div>
                                    <span className="text-[10px] uppercase tracking-[0.1em] text-[#777] min-w-0 shrink truncate whitespace-nowrap">· {timeAgo(p.created_at)}{p.reply_count ? ` · ${p.reply_count} ${p.reply_count === 1 ? "reply" : "replies"}` : ""}</span>
                                </div>
                                {hasDistinctSubject(p) ? <div className="text-sm font-semibold text-white truncate group-hover:text-[#ff4500] mt-0.5">{p.subject}</div> : null}
                                {p.body ? <div className="text-xs text-[#cfcfcf] mt-0.5 line-clamp-2">{p.body}</div> : null}
                            </div>
                        </Link>
                    );
                })}
            </div>
            <Link to={`/u/${uname}?tab=posts`} className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#ff4500] hover:opacity-80" data-testid={`feed-group-viewall-${group.key}`}>
                View all from {uname} →
            </Link>
        </div>
    );
}

/**
 * The follow feed — a reverse-chron wall of board posts from teams/trainers/events
 * the user follows. `defaultLimit` sets the initial count; a dropdown lets the user
 * change how many to show. `compact` renders a tighter card for the landing page.
 */
const FEED_SORT_KEY = "tsi-feed-sort";
const COLLAPSE_KEY = "tsi-collapse-follow";

function initialMode() {
    try {
        const saved = localStorage.getItem(FEED_SORT_KEY);
        if (saved === "recent" || saved === "top" || saved === "user") return saved;
    } catch { /* ignore */ }
    return "user";
}

export default function FeedWall({ defaultLimit = 3, compact = false }) {
    const [limit, setLimit] = useState(defaultLimit);
    const inFeedOn = useAdSetting("in_feed");
    const [mode, setMode] = useState(initialMode);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
    });

    useEffect(() => { try { localStorage.setItem(FEED_SORT_KEY, mode); } catch { /* ignore */ } }, [mode]);
    useEffect(() => { try { localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ } }, [collapsed]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const sort = mode === "top" ? "top" : "recent";
            const { data } = await api.get("/feed", { params: { limit, sort } });
            setData(data);
        } catch {
            setData({ items: [], total_sources: 0 });
        } finally { setLoading(false); }
    }, [limit, mode]);

    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="feed-wall">
            <div
                className="sticky z-30 -mx-3 md:-mx-4 px-3 md:px-4 py-2.5 flex items-center justify-between gap-3 mb-4 flex-wrap"
                style={{ top: "var(--tsi-sticky-top, 64px)", background: feedHeaderBg("following") }}
                data-testid="feed-wall-header"
            >
                <h2 className={`font-display tracking-wide inline-flex items-center gap-2 ${compact ? "text-2xl" : "text-3xl"}`}>
                    <Bookmark className="h-5 w-5 text-[#a855f7]" /> From who you follow
                    <button
                        type="button"
                        data-testid="feed-wall-collapse-toggle"
                        onClick={() => setCollapsed((v) => !v)}
                        aria-label={collapsed ? "Expand follow posts" : "Collapse follow posts"}
                        className="inline-flex items-center justify-center h-6 w-6 shrink-0 text-[#a855f7] hover:opacity-70 transition-opacity"
                    >
                        {collapsed ? <Plus className="h-5 w-5" /> : <Minus className="h-5 w-5" />}
                    </button>
                </h2>
                <div className="flex items-center gap-3 flex-wrap">
                    <select
                        data-testid="feed-sort-select"
                        value={mode}
                        onChange={(e) => setMode(e.target.value)}
                        className="bg-[#141414] border border-[#2a2a2a] text-white text-[10px] uppercase tracking-[0.2em] px-2 py-1.5 rounded-none focus:outline-none"
                    >
                        <option value="recent">Recent</option>
                        <option value="top">Top</option>
                        <option value="user">User</option>
                    </select>
                    <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
                        <select
                            data-testid="feed-count-select"
                            value={limit}
                            onChange={(e) => setLimit(Number(e.target.value))}
                            className="bg-[#141414] border border-[#2a2a2a] text-white text-xs px-2 py-1.5 rounded-none focus:outline-none"
                        >
                            {COUNT_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </label>
                </div>
            </div>

            {!collapsed && (loading && !data ? (
                <div className="text-sm text-[#a0a0a0] py-8 text-center">Loading feed…</div>
            ) : !data || data.items.length === 0 ? (
                <div className="border border-[#2a2a2a] bg-[#141414] py-10 text-center" data-testid="feed-empty">
                    <div className="text-sm text-[#a0a0a0]">
                        {data && data.total_sources === 0
                            ? "Follow teams, trainers, and events to see their board posts here."
                            : "No board posts yet from the teams, trainers, and events you follow."}
                    </div>
                </div>
            ) : (
                <div className="space-y-4" data-testid="feed-items">
                    {data.items.map((p, i) => (
                        <Fragment key={`${p._src || "board"}-${p.id}`}>
                            <FeedPostCard item={p} />
                            {inFeedOn && (i + 1) % 6 === 0 && i < data.items.length - 1 && (
                                <AdSlot slot={process.env.REACT_APP_ADSENSE_IN_FEED_SLOT} format="fluid" refreshKey={`infeed-${i}`} placement="in_feed" />
                            )}
                        </Fragment>
                    ))}
                </div>
            ))}
        </div>
    );
}
